import {useCallback, useEffect, useRef, useState} from 'react';
import {type NodeChange, type Node, useReactFlow} from '@xyflow/react';
import type {GenType} from '@features/flow/flowService';
import {trpc} from '@features/utils/trpc';
import {captureException} from '@sentry/react';
import {getEffectiveGenType} from '@features/server/utils/getEffectiveGenType';
import {
  COMPONENT_NODE_OFFSET,
  ESTIMATED_COMPONENT_SIZE,
  NODE_SPACING,
  getNodeDimensions,
} from '../utils/nodeDimensions';

export function useComponentReflow(
  flowId: string,
  flowGenType: GenType | null,
) {
  const trpcUtils = trpc.useUtils();
  const {getNodes} = useReactFlow();
  const updateScreenPositions =
    trpc.screens.updateScreenPositions.useMutation();

  const [componentDims] = useState(
    () => new Map<string, {width: number; height: number}>(),
  );
  const reflowInFlightRef = useRef(false);
  const reflowTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const performReflowRef = useRef<() => void>(() => {});

  useEffect(() => {
    const dims = componentDims;
    return () => {
      if (reflowTimeoutRef.current) {
        clearTimeout(reflowTimeoutRef.current);
        reflowTimeoutRef.current = null;
      }
      dims.clear();
      reflowInFlightRef.current = false;
    };
  }, [flowId, componentDims]);

  const performReflow = useCallback(() => {
    if (reflowInFlightRef.current) return;

    const currentScreens =
      trpcUtils.screens.getFlowScreens.getData({flowId}) ?? [];
    const unpositioned = currentScreens.filter(
      (s) => s.type === 'component' && s.x === null,
    );
    if (unpositioned.length === 0) return;

    const allMeasured = unpositioned.every((s) => componentDims.has(s.id));
    if (!allMeasured) return;

    const allContentLoaded = unpositioned.every(
      (s) =>
        trpcUtils.screens.getActiveStructure.getData({screenId: s.id}) !==
        undefined,
    );
    if (!allContentLoaded) {
      if (reflowTimeoutRef.current) {
        clearTimeout(reflowTimeoutRef.current);
      }
      reflowTimeoutRef.current = setTimeout(() => {
        reflowTimeoutRef.current = null;
        performReflowRef.current();
      }, 500);
      return;
    }

    const allNodes = getNodes();
    let rightmostScreenEdge = 0;
    for (const node of allNodes) {
      if (node.type !== 'frame') continue;
      const screenData = currentScreens.find((s) => s.id === node.id);
      const effectiveGenType = getEffectiveGenType(
        screenData?.screenSize,
        null,
        flowGenType,
      );
      const {width} = getNodeDimensions(effectiveGenType);
      rightmostScreenEdge = Math.max(
        rightmostScreenEdge,
        node.position.x + width,
      );
    }

    const allComponents = currentScreens.filter((s) => s.type === 'component');
    const componentColumnX = rightmostScreenEdge + COMPONENT_NODE_OFFSET;

    const updates: Array<{screenId: string; x: number; y: number}> = [];
    let nextY = 0;

    for (const comp of allComponents) {
      const dims = componentDims.get(comp.id);
      const height = dims?.height ?? ESTIMATED_COMPONENT_SIZE;

      if (comp.x === null) {
        updates.push({
          screenId: comp.id,
          x: Math.round(componentColumnX),
          y: Math.round(nextY),
        });
        nextY += height + NODE_SPACING;
      } else if (Math.round(comp.x) === Math.round(componentColumnX)) {
        nextY = Math.max(nextY, (comp.y ?? 0) + height + NODE_SPACING);
      }
    }

    if (updates.length === 0) return;

    reflowInFlightRef.current = true;
    void updateScreenPositions
      .mutateAsync({flowId, updates, history: 'replace'})
      .then(async () => {
        await trpcUtils.screens.getFlowScreens.cancel({flowId});
        await trpcUtils.screens.getFlowScreens.invalidate({flowId});
      })
      .catch((err: unknown) => {
        captureException(err, {data: {flowId, context: 'reflow'}});
      })
      .finally(() => {
        reflowInFlightRef.current = false;
      });
  }, [
    flowId,
    flowGenType,
    componentDims,
    trpcUtils.screens.getFlowScreens,
    trpcUtils.screens.getActiveStructure,
    getNodes,
    updateScreenPositions,
  ]);
  useEffect(() => {
    performReflowRef.current = performReflow;
  }, [performReflow]);

  const scheduleReflow = useCallback(() => {
    if (reflowTimeoutRef.current) {
      clearTimeout(reflowTimeoutRef.current);
    }
    reflowTimeoutRef.current = setTimeout(() => {
      reflowTimeoutRef.current = null;
      performReflow();
    }, 500);
  }, [performReflow]);

  const handleDimensionChanges = useCallback(
    (changes: NodeChange<Node>[]) => {
      for (const change of changes) {
        if (change.type === 'dimensions' && change.dimensions) {
          componentDims.set(change.id, {
            width: change.dimensions.width,
            height: change.dimensions.height,
          });
        }
      }

      const hasDimensionChanges = changes.some(
        (c) => c.type === 'dimensions' && c.dimensions,
      );
      if (!hasDimensionChanges) return;

      scheduleReflow();
    },
    [scheduleReflow, componentDims],
  );

  const screens = trpcUtils.screens.getFlowScreens.getData({flowId});
  const hasUnpositioned = screens?.some(
    (s) => s.type === 'component' && s.x === null,
  );
  useEffect(() => {
    if (!hasUnpositioned) return;
    scheduleReflow();
  }, [hasUnpositioned, screens, scheduleReflow]);

  const cancelPendingReflow = useCallback(() => {
    if (reflowTimeoutRef.current) {
      clearTimeout(reflowTimeoutRef.current);
      reflowTimeoutRef.current = null;
    }
  }, []);

  return {handleDimensionChanges, cancelPendingReflow, componentDims};
}
